import { ReturnComponent } from '@/utils/reactTypes';
import { Box, BoxProps, SxProps, Theme } from '@mui/material';
import { useMemo } from 'react';

// Reads as a tag rather than a second chip when it sits inside one.
const INACTIVE_TAG_FONT_SIZE = '0.5625rem';
const INACTIVE_TAG_WEIGHT = 600;
// Gap between the tag and the chip's top, bottom and right edges, in px.
const INACTIVE_TAG_INSET = 8;
// Height of a medium MUI Chip, which the tag is inset within.
const CHIP_HEIGHT = 32;

type ClinicianInactiveTagProps = Omit<BoxProps, 'children'>;

// Marks a clinician who is no longer active, alongside their name.
export const ClinicianInactiveTag = ({
    sx: providedSx,
    ...rest
}: ClinicianInactiveTagProps): ReturnComponent => {
    const sx = useMemo(
        (): SxProps<Theme> => ({
            ml: 0.75,
            px: 0.75,
            display: 'inline-flex',
            alignItems: 'center',
            height: CHIP_HEIGHT - 2 * INACTIVE_TAG_INSET,
            lineHeight: 1,
            // Fully rounded ends so the tag nests inside the chip's own rounded ends.
            borderRadius: '999px',
            backgroundColor: 'action.selected',
            color: 'text.secondary',
            fontSize: INACTIVE_TAG_FONT_SIZE,
            fontWeight: INACTIVE_TAG_WEIGHT,
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
            ...providedSx,
        }),
        [providedSx],
    );

    return (
        <Box component='span' sx={sx} {...rest}>
            INACTIVE
        </Box>
    );
};
